// ElectionStrip — "Future contributions", matching the Participant Redesign
// standalone (portfolio3.jsx FutureContributions).
//
// Promoted from a footer strip to a full card: it answers "how are my NEXT
// contributions invested?" and nothing else. Three things the design review removed
// are deliberately absent — the per-fund share of current balance, the
// "100% allocated" indicator, and the "separate from the mix you hold today"
// sentence. The By-Fund view now carries the current-vs-future comparison instead,
// as two labelled columns and a drift bar.
//
// Rendered only where the plan takes participant elections; an inactive or
// guaranteed-rate plan has no fund-level election, so the section is omitted rather
// than shown empty.
//
// MODELS. The split is stated at the level the participant elects at: a managed model
// is ONE pill (the design: "a fund inside a model has no election of its own — the
// participant elects the model and the advisor sets the mix"), beside any standalone
// funds. What each model holds sits underneath, one tab per model, so a 80/20 split
// across two models stays a two-pill split while either model's funds are a click away.
// A model directed to but not yet funded still appears in the split; its tab lists the
// published lineup instead of held weights.
//
// LONG LINEUPS. The elections stay a grid of pills — but only the leading
// INITIAL_ROWS of them, with the rest one click behind "See all N investments". A
// 10-fund election used to render as ten pills at once, which is the case that reads
// badly: the grid wraps into an uneven multi-column block that becomes the tallest
// thing on the page, and a plan can carry many more than 10. Collapsed, the card is a
// summary again. The pills are text — swatch, name, ticker, share.
//
// The stacked whole-election bar that used to sit above the grid is hidden (see the
// commented block below). The pills' stated percentages are now the only expression
// of the split, which is why they are not also collapsed to a glanceable graphic.
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Surface, Eyebrow } from '../../../reusable';
import { electionsApply, electionPct } from '../portfolio.adapters';
import { ArrowRightIcon } from '../../../icons/Icons';
import { cx } from '../../../../utils/cx';
import './election-strip.css';

// How many elections show before "See all". Five is enough to carry the shape of a
// typical election (the top few are almost always the bulk of it) while keeping the
// collapsed card short.
const INITIAL_ROWS = 5;

const shortName = (name = '') => name.replace(/^Vanguard\s+/, '');

export function ElectionStrip({ funds = [], items, models = [], plan, changedDate, onChangeElections }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState(null);

  if (!electionsApply(plan)) return null;

  // The split at election level: the holdings tree's top level (models + standalone
  // funds) when we have one, else the flat funds.
  const base = items && items.length ? items : funds;
  const rows = base.map((f) => ({ ...f, key: f.key ?? f.ticker, election: electionPct(f) }));
  // A model with contributions directed to it but nothing held yet is not a holding,
  // so the tree omits it — it is still part of where new money goes.
  models.forEach((m) => {
    if (!(m.election > 0) || rows.some((r) => r.key === m.modelId)) return;
    rows.push({
      type: 'model', key: m.modelId, name: m.name, ticker: null, color: null,
      election: m.election, children: [], lineup: m.lineup || [],
    });
  });
  // Biggest share of new money first — that is the order the question is asked in.
  rows.sort((a, b) => b.election - a.election);
  const elected = rows.filter((f) => f.election > 0);
  if (!elected.length) return null;

  // Funds you hold that new money isn't going into — worth saying out loud, since
  // the list above would otherwise look like the whole portfolio.
  const notFunded = rows.length - elected.length;

  const collapsible = elected.length > INITIAL_ROWS;
  const visible = collapsible && !expanded ? elected.slice(0, INITIAL_ROWS) : elected;

  // One tab per model ON THE PLAN: the elected ones first (carrying the tree's held
  // members), then the plan's other models as "available" — the participant can see the
  // whole menu without the split above claiming money goes anywhere it doesn't.
  const electedModels = elected.filter((r) => r.type === 'model');
  const modelRows = [
    ...electedModels,
    ...models
      .filter((m) => !electedModels.some((r) => r.key === m.modelId))
      .map((m) => ({
        type: 'model', key: m.modelId, name: m.name, color: null, election: 0,
        children: (items || []).find((i) => i.key === m.modelId)?.children || [],
        lineup: m.lineup || [],
      })),
  ];
  const activeModel = modelRows.find((m) => m.key === tab) || modelRows[0];
  const insideCount = (m) => (m.children?.length || m.lineup?.length || 0);
  const panelNoteKey = (m) => {
    if (m.children?.length) return 'portfolio.elections.setByAdvisor';
    return m.election > 0 ? 'portfolio.elections.notYetFunded' : 'portfolio.elections.availableNote';
  };

  return (
    <Surface className="election-strip">
      <header className="election-strip__head">
        <div className="election-strip__id">
          <Eyebrow>{t('portfolio.elections.eyebrow')}</Eyebrow>
          <h3 className="election-strip__title">{t('portfolio.elections.title')}</h3>
          <p className="election-strip__sub">{t('portfolio.elections.lede')}</p>
        </div>
        <div className="election-strip__meta">
          <span className="election-strip__changed">
            {t('portfolio.elections.changed', { date: changedDate || '—' })}
          </span>
          <button type="button" className="election-strip__cta" onClick={onChangeElections}>
            {t('portfolio.elections.cta')} <ArrowRightIcon width={12} height={12} />
          </button>
        </div>
      </header>

      {/* HIDDEN (frontend-only, all plans): the whole election as one stacked bar.
          Commented out rather than deleted — uncommenting restores it as-is; the
          data it drew from (`elected`) is unchanged and still feeds the grid below.
          Note the bar was already `aria-hidden`, so nothing announced is lost: the
          per-fund shares are stated as text in the pills. */}
      {/* <div className="election-strip__bar" aria-hidden="true">
        {elected.map((f) => (
          <span
            key={f.key}
            style={{ width: `${f.election}%`, background: f.color }}
            title={`${f.name} ${f.election}%`}
          />
        ))}
      </div> */}

      <div className="election-strip__grid">
        {visible.map((f) => (
          <div key={f.key} className={cx('election-strip__item', f.type === 'model' && 'election-strip__item--model')}>
            <span className="election-strip__sw" style={{ background: f.color }} />
            <span className="election-strip__nm">
              {shortName(f.name)}
              {f.type === 'model' && <span className="election-strip__tag">{t('portfolio.elections.modelTag')}</span>}
            </span>
            <span className="election-strip__tkr">
              {f.type === 'model' ? t('portfolio.elections.modelSub', { count: insideCount(f) }) : f.ticker}
            </span>
            <span className="election-strip__pct">{f.election}%</span>
          </div>
        ))}
      </div>

      {collapsible && (
        <button
          type="button"
          className="election-strip__more"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded
            ? t('portfolio.elections.seeFewer')
            : t('portfolio.elections.seeAll', { count: elected.length })}
        </button>
      )}

      {notFunded > 0 && (
        <div className="election-strip__foot">
          {t(
            notFunded === 1
              ? 'portfolio.elections.notFundedOne'
              : 'portfolio.elections.notFundedMany',
            { count: notFunded },
          )}
        </div>
      )}

      {/* What sits inside each elected model — one tab per model, the split above
          untouched. Held members show their weight in the model; an unfunded model
          shows its published lineup. */}
      {activeModel && (
        <div className="election-strip__models">
          <div className="election-strip__tabs" role="tablist" aria-label={t('portfolio.elections.modelTabsAria')}>
            {modelRows.map((m) => (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={m.key === activeModel.key}
                className={cx('election-strip__tab', m.key === activeModel.key && 'is-active')}
                onClick={() => setTab(m.key)}
              >
                <span className="election-strip__tab-sw" style={{ background: m.color }} />
                {m.name}
                <span className={cx('election-strip__tab-pct', !(m.election > 0) && 'is-available')}>
                  {m.election > 0 ? `${m.election}%` : t('portfolio.elections.availableTag')}
                </span>
              </button>
            ))}
          </div>
          <div className="election-strip__panel" role="tabpanel">
            <div className="election-strip__panel-head">
              <span className="election-strip__panel-title">
                {t('portfolio.elections.insideModel', { name: activeModel.name })}
              </span>
              <span className="election-strip__panel-note">{t(panelNoteKey(activeModel))}</span>
            </div>
            <div className="election-strip__grid election-strip__grid--inside">
              {activeModel.children?.length
                ? activeModel.children.map((c) => (
                  <div key={c.key ?? c.ticker} className="election-strip__item election-strip__item--inside">
                    <span className="election-strip__sw" style={{ background: c.color }} />
                    <span className="election-strip__nm">{shortName(c.name)}</span>
                    <span className="election-strip__tkr">{c.ticker}</span>
                    <span className="election-strip__pct" title={t('portfolio.elections.weightInModel')}>
                      {c.weightLabel ?? (c.weightInModel != null ? `${c.weightInModel}%` : '—')}
                    </span>
                  </div>
                ))
                : (activeModel.lineup || []).map((c) => (
                  <div key={c.ticker} className="election-strip__item election-strip__item--inside">
                    <span className="election-strip__sw" />
                    <span className="election-strip__nm">{shortName(c.name || c.ticker)}</span>
                    <span className="election-strip__tkr">{c.ticker}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </Surface>
  );
}

const fundShape = PropTypes.shape({
  ticker: PropTypes.string,
  name: PropTypes.string,
  color: PropTypes.string,
  election: PropTypes.number,
});

ElectionStrip.propTypes = {
  /** Flat investable funds — the split when no holdings tree is supplied. */
  funds: PropTypes.arrayOf(fundShape),
  /** Holdings tree top level (models + standalone funds, adapter `holdingsTree`). */
  items: PropTypes.arrayOf(PropTypes.object),
  /** Adapter `models` — supplies directed-but-unfunded models the tree cannot hold. */
  models: PropTypes.arrayOf(PropTypes.object),
  /** Plan record — its `actions.canChangeElections` decides whether this renders. */
  plan: PropTypes.object,
  changedDate: PropTypes.string,
  onChangeElections: PropTypes.func,
};

export default ElectionStrip;
